import { useState } from "react";
import type { IntegrationFormProps } from "./types.js";

/**
 * Plex API integration sub-form (ADR-0085 Phase 2 §5.5).
 *
 * Renders URL + User ID + Key fields with the help text specified in the spec.
 * Show/Hide key toggle on the Key input. Per-field validation errors render
 * directly below the offending input (not buried in help text).
 */

interface PlexConfigShape {
  url?: string;
}

interface PlexCredsShape {
  user_id?: string;
  key?: string; // input-only; never present in the view payload
}

export function PlexIntegrationForm(props: IntegrationFormProps): JSX.Element {
  const config = props.config as PlexConfigShape;
  const credentials = (props.credentials ?? {}) as PlexCredsShape;
  const errors = props.errors ?? {};
  const [showKey, setShowKey] = useState(false);

  const url = config.url ?? "";
  const userId = credentials.user_id ?? "";
  const key = credentials.key ?? "";

  const emit = (next: { url?: string; user_id?: string; key?: string }) => {
    props.onChange({
      config: { url: next.url ?? url },
      credentials: {
        user_id: next.user_id ?? userId,
        // key intentionally omitted when empty so PATCH preserves the existing one.
        ...(next.key !== undefined ? { key: next.key } : key ? { key } : {})
      }
    });
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label className="field">
        <span className="label">URL</span>
        <input
          className="input"
          type="text"
          value={url}
          onChange={(e) => emit({ url: e.target.value })}
          placeholder="https://cloud.plex.com"
        />
        {errors.url ? <div className="help" style={{ color: "var(--danger-text)" }}>{errors.url}</div> : null}
        <div className="help">
          Your Plex API host root — e.g. <code>https://cloud.plex.com</code> (multi-tenant cloud) or
          <code> https://&lt;your-PCN&gt;.on.plex.com</code> (tenant-specific). Scheme + host only —
          Atlas appends the data-source path automatically.
        </div>
      </label>

      <label className="field">
        <span className="label">User ID</span>
        <input
          className="input"
          type="text"
          value={userId}
          onChange={(e) => emit({ user_id: e.target.value })}
        />
        {errors.user_id ? <div className="help" style={{ color: "var(--danger-text)" }}>{errors.user_id}</div> : null}
        <div className="help">
          The Plex API username. This is a service account provisioned by Plex Customer Care, not your interactive login.
        </div>
      </label>

      <label className="field">
        <span className="label">Key</span>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            className="input"
            type={showKey ? "text" : "password"}
            value={key}
            onChange={(e) => emit({ key: e.target.value })}
            placeholder={props.mode === "edit" ? "Leave blank to keep existing key" : ""}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn"
            onClick={() => setShowKey((v) => !v)}
            aria-label={showKey ? "Hide key" : "Show key"}
          >
            {showKey ? "Hide" : "Show"}
          </button>
        </div>
        {errors.key ? <div className="help" style={{ color: "var(--danger-text)" }}>{errors.key}</div> : null}
        <div className="help">
          The password paired with the User ID. Stored encrypted; never displayed after save.
        </div>
      </label>
    </div>
  );
}
